import { DraftWaypointStatus } from '@caresend/types';
import { getPluralCount, hasStatus, initTimeZoneDate, getStreetNameByLocation, formatTimeZoneDate } from '@caresend/utils';
import { computed } from 'vue';
import { draftWaypointDetailsRoute } from '../locations.js';
import { schedulingFlowRoute } from '../schedulingFlow/locations.js';
import { getDraftWaypointDetailsBackLocation } from './helpers.js';
import { VisitsPageRouteName } from './model.js';
import { isPatientUser } from '../../../store/helpers.js';
import { getStore } from '../../../store/helpers/initStore.js';
import { useDraftWaypointsStore } from '../../../store/pinia/draftWaypoints/index.js';
import { isCancelVisitDryRun } from '../../../store/pinia/draftWaypoints/helpers.js';

// Paths below are not full paths - they are based on the parent path:
// /:partnerName?/edit-order/:draftOrderID?
const routesVisitsPage = [
    {
        path: '/:partnerName?/office/visits/',
        name: VisitsPageRouteName.VISITS_PAGE,
        props: true,
        component: () => import('../../../components/specific/VisitsPage/VisitsPage.vue.js'),
        meta: {
            authRequired: true,
        },
    },
    {
        path: '/:partnerName?/visit/:draftWaypointID/',
        name: VisitsPageRouteName.DRAFT_WAYPOINT_DETAILS,
        props: true,
        component: () => import('../../../components/specific/DraftWaypointDetails/DraftWaypointDetails.vue.js'),
        meta: {
            allowIncomplete: true,
            authRequired: true,
            heading: ({ route }) => computed(() => {
                var _a;
                const draftWaypointStore = useDraftWaypointsStore();
                const { getters } = draftWaypointStore;
                const { draftWaypointID } = route.params;
                const draftWaypoint = draftWaypointID
                    ? getters.getDenormalizedDraftWaypoint(draftWaypointID)
                    : undefined;
                const backLocation = getDraftWaypointDetailsBackLocation(draftWaypoint);
                if (!draftWaypoint || !draftWaypointID)
                    return { title: '', backLocation };
                const patientNumber = getters.getNumberOfPatientsOnVisit(draftWaypointID);
                let title = patientNumber === 0
                    ? 'Visit'
                    : `Visit for ${getPluralCount(patientNumber, 'patient', true)}`;
                if (!hasStatus(draftWaypoint.status, [
                    DraftWaypointStatus.PENDING,
                    DraftWaypointStatus.REJECTED,
                ])) {
                    const facilityDetails = draftWaypointStore.getters
                        .getFacilityDetailsOnDraftWaypoint(draftWaypoint.id);
                    if (facilityDetails) {
                        title = `Visit to ${facilityDetails.name}`;
                    }
                    else {
                        const { canceled, date } = draftWaypoint;
                        const visitCanceled = hasStatus(draftWaypoint, DraftWaypointStatus.CANCELED);
                        // The date used for the title is the finalized date, unless the visit was
                        // canceled before it was finalized.
                        const finalizedDate = date;
                        const canceledDate = (canceled === null || canceled === void 0 ? void 0 : canceled.timestamp) ? initTimeZoneDate((_a = draftWaypoint.location) === null || _a === void 0 ? void 0 : _a.timeZone, canceled === null || canceled === void 0 ? void 0 : canceled.timestamp) : undefined;
                        const location = draftWaypoint === null || draftWaypoint === void 0 ? void 0 : draftWaypoint.location;
                        const locationDisplayed = (location === null || location === void 0 ? void 0 : location.addressComponents)
                            ? ` at ${getStreetNameByLocation(location)}`
                            : '';
                        const formattedCanceledDate = canceledDate
                            ? `Visit canceled on ${formatTimeZoneDate(canceledDate, 'MMMM D')}`
                            : 'Visit canceled';
                        const formattedVisitDate = finalizedDate
                            ? `Visit${locationDisplayed} on ${formatTimeZoneDate(finalizedDate, 'MMMM D')}`
                            : 'Visit on TBA';
                        title = visitCanceled && !finalizedDate
                            ? formattedCanceledDate
                            : formattedVisitDate;
                    }
                }
                const displayKebab = computed(() => !isPatientUser());
                return {
                    overflowVisible: true,
                    title,
                    tertiaryComponent: displayKebab.value
                        ? () => import('../../../components/specific/DraftWaypointDetails/VisitDetailsKebabButton.vue.js')
                        : undefined,
                    tertiaryComponentProps: displayKebab.value
                        ? ({ denormalizedDraftWaypoint: draftWaypoint })
                        : undefined,
                    backLocation,
                };
                // Above type-cast is for vite/vue-tsc
            }),
            loadingMessage: () => 'Loading visit…',
            whiteBackground: true,
        },
    },
    {
        path: '/:partnerName?/reschedule/:draftWaypointID/',
        name: VisitsPageRouteName.RESCHEDULE_VISIT,
        props: true,
        component: () => import('../../../components/specific/RescheduleVisit/RescheduleVisit.vue.js'),
        meta: {
            allowIncomplete: true,
            authRequired: true,
            heading: ({ route }) => computed(() => {
                const draftWaypointStore = useDraftWaypointsStore();
                const { getters } = draftWaypointStore;
                const { draftWaypointID } = route.params;
                const backLocation = draftWaypointID
                    ? draftWaypointDetailsRoute({ params: { draftWaypointID } })
                    : undefined;
                const isReschedulePossible = computed(() => draftWaypointID
                    ? getters.getIsReschedulingPossible(draftWaypointID)
                    : false);
                const rescheduleRoute = computed(() => isReschedulePossible.value && draftWaypointID
                    ? schedulingFlowRoute(draftWaypointID, undefined, true)
                    : undefined);
                return {
                    backLocation,
                    backButtonText: 'Back to visit details',
                    confirmDisabled: computed(() => !isReschedulePossible.value),
                    confirmText: 'Reschedule',
                    ...(isReschedulePossible.value && {
                        confirmTo: rescheduleRoute.value,
                    }),
                    leftAlignTitle: true,
                    mobileFooter: true,
                };
            }),
            whiteBackground: true,
        },
    },
    {
        path: '/:partnerName?/cancel/:draftWaypointID/',
        name: VisitsPageRouteName.CANCEL_VISIT,
        props: true,
        component: () => import('../../../components/specific/CancelVisit/CancelVisit.vue.js'),
        meta: {
            allowIncomplete: true,
            authRequired: true,
            heading: ({ route }) => computed(() => {
                const { draftWaypointID } = route.params;
                const store = getStore();
                const draftWaypoint = draftWaypointID
                    ? store.state.waypoint.draftWaypoints[draftWaypointID]
                    : undefined;
                const isDryRun = isCancelVisitDryRun(draftWaypoint);
                const title = isDryRun ? 'Cancel visit (dry run)' : 'Cancel visit';
                const backLocation = draftWaypointID
                    ? draftWaypointDetailsRoute({ params: { draftWaypointID } })
                    : undefined;
                return {
                    title,
                    backLocation,
                };
            }),
            whiteBackground: true,
        },
    },
];
const getRoutesVisitsPage = () => routesVisitsPage;

export { getRoutesVisitsPage };
